
import React, { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  Download, 
  Calendar, 
  BarChart
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { SubsidyUsage, getAllSubsidyUsages } from "@/lib/db/subsidyDb";
import { format } from "date-fns";

const UsageReportsTab: React.FC = () => {
  const [usages, setUsages] = useState<SubsidyUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const { toast } = useToast();
  
  useEffect(() => {
    const loadUsages = async () => {
      setIsLoading(true);
      try {
        const data = await getAllSubsidyUsages();
        setUsages(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading subsidy usages:", error);
        toast({
          variant: "destructive",
          title: "Failed to load usage reports",
          description: "Could not retrieve subsidy usage data. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadUsages();
  }, [toast]);
  
  const filteredUsages = usages
    .filter(usage => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          usage.beneficiaryName.toLowerCase().includes(searchLower) ||
          usage.productName.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(usage => {
      // Apply product category filter
      if (filterCategory !== "all") {
        return usage.productCategory.toLowerCase() === filterCategory.toLowerCase();
      }
      return true;
    });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Usage Reports</CardTitle>
              <CardDescription>
                Track how subsidies are being used across products and beneficiaries.
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline">
                <BarChart className="mr-2 h-4 w-4" />
                View Charts
              </Button>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export Report
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by beneficiary or product..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Food Essentials">Food Essentials</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Household">Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select defaultValue="all">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this-week">This Week</SelectItem>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="last-month">Last Month</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            filteredUsages.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-muted-foreground">No subsidy usage data found.</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Beneficiary</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Subsidy Amount (RM)</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Outlet</TableHead>
                      <TableHead>POS Terminal</TableHead>
                      <TableHead>Cashier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsages.map((usage) => (
                      <TableRow key={usage.usageId}>
                        <TableCell>{usage.beneficiaryName}</TableCell>
                        <TableCell>{usage.productName}</TableCell>
                        <TableCell>{usage.productCategory}</TableCell>
                        <TableCell>{usage.subsidyAmount.toFixed(2)}</TableCell>
                        <TableCell>{format(new Date(usage.usageDate), "dd/MM/yyyy")}</TableCell>
                        <TableCell>{usage.outletName}</TableCell>
                        <TableCell>{usage.posTerminal}</TableCell>
                        <TableCell>{usage.cashier}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsageReportsTab;
